import React from 'react'
import classnames from 'classnames'
import { compose } from 'redux'
import {
  makeForm,
  TextFormField,
  SelectDropdownFormField,
  MaskedFormField,
  RadioFormField,
  BaseComponent
} from 'belle-js'
import Analytics from 'analytics'
import {
  countryList,
  employmentStatuses,
  workStatuses,
  occupations,
  yesOrNo
} from 'utils/fieldOptions'

import withFormResponseHandler from '../withFormResponseHandler'
import styles from 'components/Onboarding/FormStyles.module.scss'

class EmploymentDetailsForm extends BaseComponent {

  render () {
    const {
      formValid,
      formData,
      isMobile,
      onClick,
      isProspect,
    } = this.props

    const primaryButtonClasses = classnames(
      'button',
      'button--primary',
      !formValid ? 'disabled' : null
    )

    const employmentStatusValues = employmentStatuses.map(s => s.value)
    let disableEmploymentFields = true

    if (employmentStatusValues.includes(formData.employmentStatus)) {
      disableEmploymentFields = false
    }

    let showEmploymentDetailsFields = true
    if (formData.employmentStatus &&
      !employmentStatusValues.includes(formData.employmentStatus)) {
      showEmploymentDetailsFields = false
    }

    const headingStylesConfig = {}
    headingStylesConfig[styles.headingDisabled] = disableEmploymentFields
    const headingStyles = classnames(
      styles.heading,
      headingStylesConfig
    )

    const handleSubmitClick = () => {
      onClick()
      Analytics.track('User clicks Save and Continue Employment Details')
    }

    const firstRegulatoryQuestion = `${isProspect ? 'Are you' : 'Is this person'} affiliated with or employed by a stock exchange or member firm of an exchange or FINRA, or a municipal securities broker-dealer?`
    const secondRegulatoryQuestion = `${isProspect ? 'Are you' : 'Is this person'} or a member of ${isProspect ? 'your' : 'their'} household/immediate family a 10% shareholder, policy-making officer or member of the board of directors of a publicly traded company?`

    return (
      <div className={styles.Form}>
        <div className={styles.formSection}>
          <p className={styles.heading}>Employment Status</p>
          <SelectDropdownFormField
            name="employmentStatus"
            label="Employment Status"
            className={styles.FormField}
            placeholder="Select Status"
            options={workStatuses}
            value={formData.employmentStatus}
            testId="employmentDetailsForm__employmentStatus__field"
            required={false} />
        </div>

        {showEmploymentDetailsFields &&
          <div>
            <div className={styles.largeFormSection}>
              <div className={styles.twoColumn}>
                <div>
                  <p className={headingStyles}>Occupation</p>
                  <SelectDropdownFormField
                    className={isMobile ? styles.FormFieldInSection : null}
                    name="occupation"
                    label="Occupation"
                    placeholder="Select Occupation"
                    options={occupations}
                    disabled={disableEmploymentFields}
                    value={formData.occupation || ''}
                    testId="employmentDetailsForm__occupation__field"
                    required={false} />
                </div>
                <div>
                  <p className={headingStyles}>Employer</p>
                  <TextFormField
                    disabled={disableEmploymentFields}
                    name="currentEmployer"
                    label="Current Employer"
                    maxLength={50}
                    placeholder="Current employer"
                    value={formData.currentEmployer || ''}
                    testId="employmentDetailsForm__currentEmployer__field"
                    required={false} />
                </div>
              </div>
            </div>

            <div className={styles.formSection}>
              <p className={headingStyles}>Business Address</p>

              <div className={styles.twoColumn}>
                <div>
                  <TextFormField
                    disabled={disableEmploymentFields}
                    name="personOtherStreet"
                    label="Primary Address"
                    className={styles.FormFieldInSection}
                    maxLength={255}
                    placeholder="Street name and number"
                    value={formData.personOtherStreet || ''}
                    testId="employmentDetailsForm__businessStreet__field"
                    required={false} />
                  <TextFormField
                    disabled={disableEmploymentFields}
                    name="personOtherCity"
                    label="personOther City"
                    className={styles.FormFieldInSection}
                    maxLength={40}
                    placeholder="City"
                    validations={{ isValidEntityName: true }}
                    validationErrors={{ isValidEntityName: 'Hmm, that’s not a valid city.' }}
                    value={formData.personOtherCity || ''}
                    testId="employmentDetailsForm__businessCity__field"
                    required={false} />
                  {!isMobile &&
                  <TextFormField
                    disabled={disableEmploymentFields}
                    name="personOtherPostalCode"
                    label="personOther Postal"
                    maxLength={20}
                    placeholder="Zipcode"
                    validations={{ isValidPostalCode: true }}
                    validationErrors={{ isValidPostalCode: 'Your zipcode may contain only numbers, letters and dashes.' }}
                    value={formData.personOtherPostalCode || ''}
                    testId="employmentDetailsForm__businessPostalCode__field"
                    required={false} />}
                </div>
                <div>
                  <TextFormField
                    disabled={disableEmploymentFields}
                    name="personOtherState"
                    label="State"
                    maxLength={80}
                    placeholder="State"
                    value={formData.personOtherState || ''}
                    className={styles.FormFieldInSection}
                    validations={{ isValidEntityName: true }}
                    validationErrors={{ isValidEntityName: 'Hmm, that’s not a valid state.' }}
                    testId="employmentDetailsForm__businessState__field"
                    required={false} />
                  {isMobile &&
                  <TextFormField
                    className={styles.FormFieldInSection}
                    disabled={disableEmploymentFields}
                    name="personOtherPostalCode"
                    label="personOther Postal"
                    maxLength={20}
                    placeholder="Zipcode"
                    validations={{ isValidPostalCode: true }}
                    validationErrors={{ isValidPostalCode: 'Your zipcode may contain only numbers, letters and dashes.' }}
                    testId="employmentDetailsForm__businessPostalCode__field"
                    value={formData.personOtherPostalCode || ''}
                    required={false} />}
                  <SelectDropdownFormField
                    disabled={disableEmploymentFields}
                    name="personOtherCountry"
                    label="Country"
                    placeholder="Select Country"
                    options={countryList}
                    value={formData.personOtherCountry || 'United States'}
                    testId="employmentDetailsForm__businessCountry__field"
                    required={false} />
                </div>
              </div>
            </div>

            <div className={styles.twoColumn}>
              <div className={styles.largeFormSection}>
                <p className={headingStyles}>Business Phone Number</p>
                <MaskedFormField
                  disabled={disableEmploymentFields}
                  type="tel"
                  name="personOtherPhone"
                  label="Business Phone Number"
                  placeholder="555-666-7777"
                  mask="phoneNumber"
                  required={false}
                  validations={{ isValidPhoneNumber: true }}
                  validationErrors={{ isValidPhoneNumber: 'Hmm, that’s not a valid phone number.' }}
                  testId="employmentDetailsForm__businessPhoneNumber__field"
                  value={formData.personOtherPhone || ''} />
              </div>
              <div className={styles.formSection} />
            </div>
          </div>
        }

        <p className={styles.regulatoryHeading}>Regulatory Questions</p>

        <div className={styles.smallFormSection}>
          <div className={styles.twoColumn}>
            <div className={styles.regulatory}>
              <p className={styles.question}>
                {firstRegulatoryQuestion}
              </p>
            </div>
            <div className={styles.radioBlock}>
              <RadioFormField
                name="finraAffiliated"
                horizontal={false}
                options={yesOrNo}
                label="Finra Affiliation"
                required={false}
                testId="employmentDetailsForm__affiliationFinraStockExBD__field"
                value={formData.finraAffiliated} />
            </div>
          </div>
        </div>

        <div className={styles.largeFormSection}>
          <div className={styles.twoColumn}>
            <div className={styles.regulatory}>
              <p className={styles.question}>
                {secondRegulatoryQuestion}
              </p>
            </div>
            <div className={styles.radioBlock}>
              <RadioFormField
                name="publicCompanyAffiliated"
                horizontal={false}
                options={yesOrNo}
                label="Public Company Affiliation"
                required={false}
                testId="employmentDetailsForm__director10ShPmo__field"
                value={formData.publicCompanyAffiliated} />
            </div>
          </div>
        </div>
        {
          /* eslint-disable react/button-has-type */
          <button
            className={primaryButtonClasses}
            type={formValid ? 'submit' : 'button'}
            onClick={handleSubmitClick}
            data-test-id="employmentDetailsForm__saveAndContinue__cta">
             Save and Continue
          </button>
        }
      </div>
    )
  }

}

export default compose(
  makeForm,
  withFormResponseHandler,
)(EmploymentDetailsForm)



// WEBPACK FOOTER //
// ./src/components/Onboarding/EmploymentDetails/EmploymentDetailsForm.jsx